import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Building2, Trash2, Users, Pencil, MapPin, Plus, X } from "lucide-react";

const emptyForm = { username: "", name: "", password: "", email: "", phone: "", branch_venues: [] };

function VenueEditor({ venues, setVenues, suggestions, testid }) {
  const [text, setText] = useState("");
  const add = (v) => {
    const val = (v ?? text).trim();
    if (val && !venues.includes(val)) setVenues([...venues, val]);
    setText("");
  };
  const remove = (v) => setVenues(venues.filter((x) => x !== v));
  const unused = suggestions.filter((s) => !venues.includes(s));
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-2">
        {venues.length === 0 && <span className="text-xs text-slate-400">No venues assigned yet.</span>}
        {venues.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full pl-2.5 pr-1 py-1 text-xs font-medium">
            <MapPin size={11} />{v}
            <button type="button" onClick={() => remove(v)} className="hover:bg-emerald-200 rounded-full p-0.5" data-testid={`venue-remove-${v}`}><X size={11} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a venue and Add"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="venue-input" />
        <Button type="button" variant="outline" onClick={() => add()} data-testid="venue-add-btn"><Plus size={14} /></Button>
      </div>
      {unused.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {unused.map((s) => (
            <button key={s} type="button" onClick={() => add(s)} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full px-2.5 py-1" data-testid={`venue-suggest-${s}`}>+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BranchManagers() {
  const [bms, setBms] = useState([]);
  const [venues, setVenues] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyForm);
  const [editBm, setEditBm] = useState(null);
  const [ef, setEf] = useState({ name: "", email: "", phone: "", password: "", branch_venues: [] });

  const load = () => api.get("/branch-managers").then((r) => setBms(r.data));
  useEffect(() => {
    load();
    api.get("/melas").then((r) => setVenues([...new Set((r.data || []).map((m) => m.venue).filter(Boolean))]));
  }, []);

  const create = async () => {
    if (!f.username || !f.name || !f.password) return toast.error("Username, name and password required");
    try {
      await api.post("/branch-managers", f);
      toast.success("Branch Manager created"); setOpen(false); setF(emptyForm); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openEdit = (b) => {
    setEditBm(b);
    setEf({ name: b.name, email: b.email || "", phone: b.phone || "", password: "", branch_venues: b.branch_venues || [] });
  };
  const saveEdit = async () => {
    const payload = { name: ef.name, email: ef.email, phone: ef.phone, branch_venues: ef.branch_venues };
    if (ef.password) payload.password = ef.password;
    try {
      await api.patch(`/branch-managers/${editBm.id}`, payload);
      toast.success("Branch Manager updated"); setEditBm(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/branch-managers/${id}`); toast.success("Branch Manager removed"); load(); };

  return (
    <div>
      <PageHeader title="Branch Managers" subtitle="Create branch managers and assign them mela venues. They control every RM, cost and lead at their venue."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#0F4C81] hover:bg-[#0A3A66]" data-testid="create-bm-btn"><Building2 size={16} className="mr-2" />Create Branch Manager</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Branch Manager</DialogTitle><DialogDescription>Assign one or more mela venues. The BM will manage all RMs and data at those venues.</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div><Label className="mb-1.5 block">Full Name</Label><Input data-testid="bm-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Username</Label><Input data-testid="bm-username" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Password</Label><Input type="text" data-testid="bm-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
                <div className="col-span-2"><Label className="mb-1.5 block">Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
                <div className="col-span-2"><Label className="mb-1.5 block">Assigned Mela Venue(s)</Label><VenueEditor venues={f.branch_venues} setVenues={(v) => setF({ ...f, branch_venues: v })} suggestions={venues} testid="bm-venues" /></div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="save-bm-btn" className="bg-[#0F4C81] hover:bg-[#0A3A66]">Create Account</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {bms.map((b) => (
          <Card key={b.id} className="p-6" data-testid={`bm-card-${b.username}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#0A192F] text-emerald-400 flex items-center justify-center"><Building2 size={20} /></div>
              <div><div className="font-bold text-slate-900">{b.name}</div><div className="text-xs text-slate-400">@{b.username}</div></div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <Users size={14} className="text-slate-400" />
              <span className="text-slate-400">RMs under this branch:</span>
              <span className="font-mono font-bold text-slate-800">{b.rm_count}</span>
            </div>
            <div className="mt-3">
              <div className="text-xs text-slate-400 mb-1.5">Assigned Venue(s)</div>
              <div className="flex flex-wrap gap-1.5">
                {(b.branch_venues || []).length === 0 && <span className="text-xs text-rose-500">None assigned</span>}
                {(b.branch_venues || []).map((v) => (
                  <span key={v} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-0.5 text-xs font-medium" data-testid={`bm-venue-${b.username}-${v}`}><MapPin size={11} />{v}</span>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(b)} data-testid={`edit-bm-${b.username}`}><Pencil size={14} className="mr-1" />Edit</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="flex-1 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`delete-bm-${b.username}`}><Trash2 size={14} className="mr-1" />Remove</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Remove {b.name}?</AlertDialogTitle><AlertDialogDescription>This deactivates the branch manager account. The RMs and their data at the branch remain untouched.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(b.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-bm-${b.username}`}>Remove</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        ))}
        {bms.length === 0 && <div className="text-slate-400">No branch managers yet. Create the first one.</div>}
      </div>

      <Dialog open={!!editBm} onOpenChange={(o) => !o && setEditBm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit {editBm?.name}</DialogTitle><DialogDescription>Update details, assigned venues, or reset the password.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label className="mb-1.5 block">Full Name</Label><Input data-testid="edit-bm-name" value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Phone</Label><Input value={ef.phone} onChange={(e) => setEf({ ...ef, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label className="mb-1.5 block">Email</Label><Input value={ef.email} onChange={(e) => setEf({ ...ef, email: e.target.value })} /></div>
            <div className="col-span-2"><Label className="mb-1.5 block">Assigned Mela Venue(s)</Label><VenueEditor venues={ef.branch_venues} setVenues={(v) => setEf({ ...ef, branch_venues: v })} suggestions={venues} testid="edit-bm-venues" /></div>
            <div className="col-span-2"><Label className="mb-1.5 block">New Password <span className="text-slate-400 font-normal">(leave blank to keep current)</span></Label><Input type="password" data-testid="edit-bm-password" value={ef.password} onChange={(e) => setEf({ ...ef, password: e.target.value })} placeholder="••••••••" /></div>
          </div>
          <DialogFooter><Button onClick={saveEdit} data-testid="save-edit-bm-btn" className="bg-[#0F4C81] hover:bg-[#0A3A66]">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
